//optonpo-ui/src/js/client/samples.js

import Flickity from "flickity";
import imagesLoaded from "imagesloaded";

const pdfCache = new Map();
const pdfOptions = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist@latest/cmaps/',
  cMapPacked: true
};

const createLoadingPlaceholder = () => {
  const placeholder = document.createElement('div');
  placeholder.className = 'loading-placeholder d-flex align-items-center justify-content-center';

  const logo = document.createElement('img');
  logo.src = '/assets/img/Opto_Logo1_Warn.svg';
  logo.className = 'animated-logo';
  logo.alt = 'Opto Loading';

  placeholder.appendChild(logo);
  return placeholder;
};

const handleScroll = () => {
  const viewer = document.getElementById('viewer');
  if (document.body.getBoundingClientRect().top >= 0) {
    viewer.style.position = 'relative';
  }
};

const scrollToFilter = () => {
  const gridFilter = document.getElementById('grid-filter');
  if (gridFilter) {
    const filterTop = gridFilter.getBoundingClientRect().top;
    const offset = 100;
    window.scrollTo({
      top: window.scrollY + filterTop - offset,
      behavior: 'smooth'
    });
  }
};

const handleSubmit = (e) => {
  e.preventDefault();
  scrollToFilter();
};

const handleGridClick = (e) => {
  const gridItem = e.target.closest('.grid-item');
  if (!gridItem) return;

  scrollToFilter();
  const viewer = document.getElementById('viewer');
  viewer.style.position = 'sticky';
  viewer.style.bottom = '2rem';

  updateViewer(gridItem);
};

const updateViewer = async (gridItem) => {
  const thumbnail = gridItem.querySelector('img');
  if (!thumbnail) {
    console.error('Thumbnail not found for grid item:', gridItem);
    return;
  }

  const filename = thumbnail.src.split('/').pop();
  console.log('Updating viewer for file:', filename);

  try {
    const API_URL = 'https://optonpo.com';
    if (!gridItem.dataset.metadata) {
      console.log('Fetching metadata for:', filename);
      const response = await fetch(`${API_URL}/api/sample-data/${filename}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch metadata: ${response.status}`);
      }
      const metadata = await response.json();
      gridItem.dataset.keywords = metadata.keywords.join(', ');
      gridItem.dataset.metadata = JSON.stringify(metadata);
    }

    console.log('Clearing existing viewers...');
    const viewerElements = {
      sampleSlider: document.getElementById('sampleSlider'),
      detailSlider: document.getElementById('detailSlider'),
    };

    if (!viewerElements.sampleSlider || !viewerElements.detailSlider) {
      console.error('Viewer elements not found in the DOM.');
      return;
    }

    // Destroy existing Flickity instances if they exist
    if (viewerElements.sampleSlider.flickityInstance) {
      viewerElements.sampleSlider.flickityInstance.destroy();
      viewerElements.sampleSlider.flickityInstance = null;
    }

    if (viewerElements.detailSlider.flickityInstance) {
      viewerElements.detailSlider.flickityInstance.destroy();
      viewerElements.detailSlider.flickityInstance = null;
    }

    // Clear existing content
    viewerElements.sampleSlider.innerHTML = '';
    viewerElements.detailSlider.innerHTML = '';

    // Add a placeholder for the thumbnail
    const thumbnailPlaceholder = document.createElement('div');
    thumbnailPlaceholder.style.width = '100%';
    thumbnailPlaceholder.style.height = '100%';
    thumbnailPlaceholder.style.backgroundImage = `url(${thumbnail.src})`;
    thumbnailPlaceholder.style.backgroundSize = 'contain';
    thumbnailPlaceholder.style.backgroundRepeat = 'no-repeat';
    thumbnailPlaceholder.style.backgroundPosition = 'center';
    viewerElements.sampleSlider.appendChild(thumbnailPlaceholder);

    console.log('Thumbnail placeholder added.');

    // Parse metadata
    const metadata = JSON.parse(gridItem.dataset.metadata);
    console.log('Metadata parsed:', metadata);

    // Add metadata details to the detail slider
    const detailContent = document.createElement('div');
    detailContent.className = 'w-100 h-100 d-flex align-items-center justify-content-center';
    detailContent.style.overflow = 'scroll';
    detailContent.innerHTML = `
      <div class="col-12 px-4 text-left">
        <h6 class="text-uppercase text-secondary mb-5">${metadata.title || 'Untitled'}</h6>
        <blockquote class="mb-3">
          <p class="text-sm text-dark" style="text-align: justify;">${metadata.subject || 'No description available'}</p>
          <p class="h6 text-uppercase text-secondary mb-0">
            Tags: ${Array.isArray(metadata.keywords) ? metadata.keywords.map(kw =>
        `<span class="keyword-tag lift" style="cursor: pointer;">${kw}</span>`
      ).join(', ') : 'None'}
          </p>
        </blockquote>
      </div>
    `;
    viewerElements.detailSlider.appendChild(detailContent);

    detailContent.querySelectorAll('.keyword-tag').forEach(tag => {
      tag.addEventListener('click', () => {
        const filterInput = document.getElementById('filterInput');
        filterInput.value = tag.textContent;
      });
    });

    // Load the PDF and render pages
    const pdfPath = thumbnail.src.replace('/thumb/', '/').replace(/\.(jpg|png)$/, '.pdf');
    console.log('Loading PDF from:', pdfPath);

    const pdf = await pdfjsLib.getDocument({ ...pdfOptions, url: pdfPath }).promise;

    // Render the first page immediately
    const firstPage = await pdf.getPage(1);
    const viewport = firstPage.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await firstPage.render({
      canvasContext: ctx,
      viewport: viewport,
    }).promise;

    const firstPageElement = document.createElement('div');
    firstPageElement.style.width = '100%';
    firstPageElement.style.height = '100%';
    firstPageElement.style.backgroundImage = `url(${canvas.toDataURL()})`;
    firstPageElement.style.backgroundSize = 'contain';
    firstPageElement.style.backgroundRepeat = 'no-repeat';
    firstPageElement.style.backgroundPosition = 'center';

    // Replace the placeholder with the first page
    thumbnailPlaceholder.replaceWith(firstPageElement);
    console.log('First page rendered and placeholder replaced.');

    // Render additional pages in the background
    for (let i = 2; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: ctx,
        viewport: viewport,
      }).promise;

      const pageElement = document.createElement('div');
      pageElement.style.width = '100%';
      pageElement.style.height = '100%';
      pageElement.style.backgroundImage = `url(${canvas.toDataURL()})`;
      pageElement.style.backgroundSize = 'contain';
      pageElement.style.backgroundRepeat = 'no-repeat';
      pageElement.style.backgroundPosition = 'center';

      viewerElements.sampleSlider.appendChild(pageElement);
    }

    // Initialize Flickity for sampleSlider
    const sampleSliderFlickity = new Flickity(viewerElements.sampleSlider, {
      prevNextButtons: true,
      pageDots: false,
      cellAlign: 'center',
      contain: true,
      adaptiveHeight: false,
      wrapAround: false,
    });

    // Store the Flickity instance for cleanup later
    viewerElements.sampleSlider.flickityInstance = sampleSliderFlickity;

    console.log('Flickity initialized for sampleSlider.');

    // Initialize Flickity for detailSlider
    const detailSliderFlickity = new Flickity(viewerElements.detailSlider, {
      fade: true,
      draggable: false,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: false,
    });

    // Store the Flickity instance for cleanup later
    viewerElements.detailSlider.flickityInstance = detailSliderFlickity;

    console.log('Flickity initialized for detailSlider.');

  } catch (error) {
    console.error('Error updating viewer:', error);
  }
};

document.addEventListener('DOMContentLoaded', async function () {
  const path = window.location.pathname;

  // Check if the current page is /samples.html
  if (typeof path !== 'string' || (!path.endsWith('/samples') && !path.endsWith('/samples.html'))) {
    return;
  }

  const gridFilter = document.getElementById('grid-filter');
  if (!gridFilter) {
    console.error('Grid filter element not found');
    return;
  }

  const grid = document.querySelector('[samples-isotope]');
  if (!grid) {
    console.error('Grid element not found');
    return;
  }

  const thumbDir = '/assets/samples/thumb';

  const API_URL = 'https://optonpo.com';

  const filterInput = document.getElementById('filterInput');
  if (!filterInput) {
    console.error('Filter input element not found');
    return;
  }

  try {
    const response = await fetch(`${API_URL}/api/sample-data`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const samples = await response.json();

    // Preload metadata for all samples
    const loadAllMetadata = async (samples) => {
      filterInput.disabled = true;
      filterInput.placeholder = 'Loading metadata...';
    
      const gridItems = document.querySelectorAll('.grid-item');
      const metadataPromises = Array.from(gridItems).map(async (item) => {
        const thumbnail = item.querySelector('img');
        const filename = thumbnail.src.split('/').pop();

        try {
          const response = await fetch(`${API_URL}/api/sample-data/${filename}`);
          const metadata = await response.json();
          item.dataset.keywords = metadata.keywords.join(', ');
          item.dataset.metadata = JSON.stringify(metadata);
        } catch (error) {
          console.error(`Metadata fetch failed for ${filename}:`, error);
        }
      });

      await Promise.all(metadataPromises);
      filterInput.disabled = false;
      filterInput.placeholder = 'Search samples...';
    };

    await loadAllMetadata(samples);

    // Security controls
    document.addEventListener('keydown', function (e) {
      if (e.key === 'p' && (e.ctrlKey || e.metaKey)) e.preventDefault();
      if (e.key === 'u' && (e.ctrlKey || e.metaKey)) e.preventDefault();
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) e.preventDefault();
    });

    document.addEventListener('contextmenu', e => e.preventDefault());
    document.addEventListener('dragstart', e => e.preventDefault());

    // Viewer setup
    const viewer = document.getElementById('viewer');
    let flkty = null;
    let detailFlkty = null;

    const viewerElements = {
      title: document.querySelector('#viewer .text-uppercase.text-dark'),
      container: document.getElementById('sampleViewer'),
      sampleSlider: document.getElementById('sampleSlider'),
      detailSlider: document.getElementById('detailSlider')
    };

    // Event listeners
    window.addEventListener('scroll', handleScroll);
    filterInput.addEventListener('focus', scrollToFilter);
    gridFilter.addEventListener('submit', handleSubmit);
    grid.addEventListener('click', handleGridClick);

  } catch (error) {
    console.error('Error loading thumbnail directory:', error);
  } finally {
    const cleanup = () => {
      window.removeEventListener('scroll', handleScroll);
      filterInput?.removeEventListener('focus', scrollToFilter);
      gridFilter?.removeEventListener('submit', handleSubmit);
      grid?.removeEventListener('click', handleGridClick);
    };

    // Attach cleanup to window unload
    window.addEventListener('unload', cleanup);
    return cleanup;
  }
});